/* ALTF4 header search + predictive panel.
 *
 * ARIA 1.2 combobox. The input owns role="combobox", the panel is the listbox, rows are
 * options. Arrow keys move aria-activedescendant; focus never leaves the input, which is
 * what lets a screen reader announce the active row instead of losing the caret.
 *
 * The panel is fetched from sections/altf4-predictive.liquid via the Section Rendering API.
 * limit_scope=each is not optional — without it the 10-result default is shared across
 * every type and the per-type split collapses at random.
 *
 * Stale responses are aborted, not merely ignored: a slow response for "ge" must never
 * paint over a fast one for "gears".
 */
(function () {
  'use strict';
  if (window.__af4SearchBound) return;
  window.__af4SearchBound = true;

  var MIN_CHARS = 2;
  var DEBOUNCE = 200;

  var wrap = document.querySelector('[data-af4-search]');
  if (!wrap) return;

  var input = wrap.querySelector('[data-af4-search-input]');
  var panel = wrap.querySelector('[data-af4-pred]');
  var live = wrap.querySelector('[data-af4-pred-live]');
  var toggle = wrap.querySelector('[data-af4-search-toggle]');
  var clearBtn = wrap.querySelector('[data-af4-search-clear]');
  var form = wrap.querySelector('form');
  if (!input || !panel) return;

  var timer = null;
  var ctrl = null;
  var activeIndex = -1;
  var lastQuery = '';

  /* ---------- open / close the field itself ---------- */

  function expand() {
    wrap.classList.add('is-expanded');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    input.focus();
  }
  function collapse() {
    closePanel();
    wrap.classList.remove('is-expanded');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }
  if (toggle) {
    toggle.addEventListener('click', function () {
      if (wrap.classList.contains('is-expanded')) collapse();
      else expand();
    });
  }

  /* ---------- panel ---------- */

  function options() { return panel.querySelectorAll('[role="option"]'); }

  function openPanel() {
    if (panel.hasAttribute('hidden')) panel.removeAttribute('hidden');
    input.setAttribute('aria-expanded', 'true');
  }

  function closePanel() {
    panel.setAttribute('hidden', '');
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    activeIndex = -1;
  }

  function setActive(i) {
    var opts = options();
    if (!opts.length) return;
    if (i < 0) i = opts.length - 1;
    if (i >= opts.length) i = 0;
    opts.forEach(function (o) { o.classList.remove('is-active'); });
    var el = opts[i];
    el.classList.add('is-active');
    if (!el.id) el.id = 'af4-pred-opt-auto-' + i;
    input.setAttribute('aria-activedescendant', el.id);
    activeIndex = i;
    if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }

  function announce() {
    if (!live) return;
    var n = options().length;
    // The "See all results" row is an option too; it is not a result.
    var results = panel.querySelectorAll('.af4-pred__row').length;
    live.textContent = n === 0 ? 'No suggestions' : results + ' suggestions available';
  }

  /* ---------- fetch ---------- */

  function suggest(q) {
    if (ctrl) ctrl.abort();
    ctrl = new AbortController();

    var url = '/search/suggest'
      + '?q=' + encodeURIComponent(q)
      + '&section_id=altf4-predictive'
      + '&resources[type]=product,collection,page'
      // 10, not 6 (13 Sep 2026): altf4-predictive.liquid skips products that do not activate in
      // the US, so it needs spare candidates to still show up to 6. Collections and pages stay
      // capped at 2 each in the Liquid.
      + '&resources[limit]=10'
      + '&resources[limit_scope]=each';

    fetch(url, { signal: ctrl.signal })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        // Guard against out-of-order paints even after an abort race.
        if (input.value.trim() !== q) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-af4-pred-inner]');
        panel.innerHTML = fresh ? fresh.innerHTML : '';
        activeIndex = -1;
        input.removeAttribute('aria-activedescendant');
        if (panel.innerHTML.trim() === '') closePanel();
        else { openPanel(); announce(); }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        closePanel();   // a failed suggest must never block the real search
      });
  }

  /* ---------- input ---------- */

  input.addEventListener('input', function () {
    var q = input.value.trim();
    if (clearBtn) { if (q) clearBtn.removeAttribute('hidden'); else clearBtn.setAttribute('hidden', ''); }

    clearTimeout(timer);
    if (q.length < MIN_CHARS) {
      if (ctrl) ctrl.abort();
      lastQuery = '';
      closePanel();
      panel.innerHTML = '';
      return;
    }
    if (q === lastQuery) return;
    lastQuery = q;
    timer = setTimeout(function () { suggest(q); }, DEBOUNCE);
  });

  input.addEventListener('keydown', function (e) {
    var opts = options();
    switch (e.key) {
      case 'ArrowDown':
        if (!opts.length) return;
        e.preventDefault();
        openPanel();
        setActive(activeIndex + 1);
        break;
      case 'ArrowUp':
        if (!opts.length) return;
        e.preventDefault();
        setActive(activeIndex - 1);
        break;
      case 'Enter':
        // With a row selected, follow it. Otherwise let the form submit to /search.
        if (activeIndex >= 0 && opts[activeIndex]) {
          e.preventDefault();
          window.location.href = opts[activeIndex].getAttribute('href');
        }
        break;
      case 'Escape':
        if (!panel.hasAttribute('hidden')) { e.preventDefault(); closePanel(); }
        else collapse();
        break;
      case 'Tab':
        closePanel();
        break;
    }
  });

  if (clearBtn) {
    clearBtn.addEventListener('click', function () {
      input.value = '';
      lastQuery = '';
      panel.innerHTML = '';
      closePanel();
      clearBtn.setAttribute('hidden', '');
      input.focus();
    });
  }

  if (form) {
    form.addEventListener('submit', function (e) {
      if (!input.value.trim()) e.preventDefault();   // an empty search is not a search
    });
  }

  // Pointer hover mirrors keyboard state so the two never disagree.
  panel.addEventListener('mousemove', function (e) {
    var row = e.target.closest('[role="option"]');
    if (!row) return;
    var opts = Array.prototype.slice.call(options());
    var i = opts.indexOf(row);
    if (i > -1 && i !== activeIndex) setActive(i);
  });

  document.addEventListener('click', function (e) {
    if (!wrap.contains(e.target)) {
      closePanel();
      if (!input.value.trim()) collapse();
    }
  });

  // "/" focuses search, the way every search-first storefront behaves — but never while
  // the shopper is already typing somewhere else.
  document.addEventListener('keydown', function (e) {
    if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
    var t = e.target;
    var tag = t && t.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
    e.preventDefault();
    expand();
  });
})();
